/**
 * World Seed Types - Dynamic World Generation System
 * Defines the structure for AI-driven scenario generation
 */

export type EnvironmentType = 'urban' | 'desert' | 'void' | 'forest' | 'arctic' | 'inferno'
export type TimeOfDay = 'day' | 'night' | 'eclipse' | 'dawn' | 'dusk'
export type AtmosphericCondition = 'clear' | 'foggy' | 'stormy' | 'toxic'
export type EnemyDensity = 'sparse' | 'moderate' | 'dense' | 'overwhelming'
export type TacticalFocus = 'stealth' | 'assault' | 'defense' | 'recon' | 'endurance'
export type PortalState = 'dormant' | 'generating' | 'locked' | 'active'

/**
 * WorldSeed - The core configuration for dynamic world generation
 * Generated by TatianaAI based on user intent
 */
export interface WorldSeed {
  environment: EnvironmentType
  time_of_day: TimeOfDay
  difficulty: number // 0.1 to 1.0
  objective: string
  scriptural_theme: string
  atmospheric_conditions?: AtmosphericCondition
  enemy_density?: EnemyDensity
  tactical_focus?: TacticalFocus
  // Vanguard Gate fields
  access?: 'granted' | 'denied'
}

/**
 * Environment color mapping for portal materials and scene lighting
 */
export const ENVIRONMENT_COLORS: Record<EnvironmentType, string> = {
  urban: '#000000',    // Pure void
  desert: '#000000',   // Pure void
  void: '#000000',     // Pure void black - Aether Void
  forest: '#000000',   // Pure void
  arctic: '#000000',   // Pure void
  inferno: '#000000'   // Pure void
}

/**
 * Time of day lighting configurations
 */
export const TIME_OF_DAY_LIGHTING: Record<TimeOfDay, {
  ambientIntensity: number
  directionalColor: string
  directionalIntensity: number
  fogColor: string
  fogDensity: number
  backgroundColor: string
}> = {
  day: {
    ambientIntensity: 0.3,
    directionalColor: '#ffffff',
    directionalIntensity: 1.0,
    fogColor: '#000000',
    fogDensity: 0.005,
    backgroundColor: '#000000'
  },
  night: {
    ambientIntensity: 0.05,
    directionalColor: '#4A90E2',
    directionalIntensity: 0.5,
    fogColor: '#000000',   // Pure void black - Aether Void
    fogDensity: 0.015,    // Atmospheric fog for depth
    backgroundColor: '#000000' // Pure void black
  },
  eclipse: {
    ambientIntensity: 0.02,
    directionalColor: '#8B0000',
    directionalIntensity: 0.3,
    fogColor: '#000000',
    fogDensity: 0.03,
    backgroundColor: '#000000'
  },
  dawn: {
    ambientIntensity: 0.15,
    directionalColor: '#FFD700',
    directionalIntensity: 0.7,
    fogColor: '#000000',
    fogDensity: 0.01,
    backgroundColor: '#000000'
  },
  dusk: {
    ambientIntensity: 0.1,
    directionalColor: '#FF4500',
    directionalIntensity: 0.6,
    fogColor: '#000000',
    fogDensity: 0.015,
    backgroundColor: '#000000'
  }
}

/**
 * Portal animation speeds (rotations per minute)
 */
export const PORTAL_ANIMATION_SPEEDS = {
  normal: 0.5,      // 0.5 RPM - standard idle
  generating: 3.0,  // 3.0 RPM - during world generation
  locked: 1.0,      // 1.0 RPM - ready for entry
  active: 0.2       // 0.2 RPM - player colliding
}

/**
 * Extended portal configuration with WorldSeed
 */
export interface PortalConfig {
  game: string
  color: string
  position: [number, number, number]
  active: boolean
  state: PortalState
  worldSeed?: WorldSeed
  generationProgress?: number // 0.0 to 1.0
}

/**
 * Scenario generation result
 */
export interface ScenarioResult {
  success: boolean
  worldSeed?: WorldSeed
  error?: string
  generationTime: number
}

/**
 * Tool call with WorldSeed for TatianaAI
 */
export interface WorldSeedToolCall {
  action: 'spawn_portal'
  game: string
  world_seed: WorldSeed
}

/**
 * User intent parsing result
 */
export interface ParsedIntent {
  environment?: EnvironmentType
  time_of_day?: TimeOfDay
  difficulty?: number
  objective?: string
  scriptural_theme?: string
  atmospheric_conditions?: AtmosphericCondition
  enemy_density?: EnemyDensity
  tactical_focus?: TacticalFocus
  confidence: number // 0.0 to 1.0
}
